import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@pilot-hub/api-client'

// WebSocket für die Handsteuerung: wss://<host>/<API-Basis>/dji/live/{sn}/sticks?token=<JWT>.
// Gesendet wird ca. 10×/s {x,y,z,r,stufe}; der Server antwortet gelegentlich {ok:true,stufe,latenz_ms}
// bzw. bei Fehler {ok:false,fehler} und schließt. Jede Störung wird an den Aufrufer gemeldet (der schaltet dann ab).

export interface StickBefehl { x: number; y: number; z: number; r: number; stufe: number }

/** JWT aus demselben Speicher wie der api-client. */
function zugangstoken(): string | null {
  try {
    const key = (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
    const p = JSON.parse(localStorage.getItem(key) || 'null')
    return p?.accessToken || p?.access_token || p?.state?.accessToken || p?.state?.access_token || null
  } catch { return null }
}

function socketUrl(sn: string, token: string) {
  const basis = String(api.defaults.baseURL || '/api/v1').replace(/\/$/, '')
  const abs = /^https?:/.test(basis) ? basis : `${window.location.origin}${basis}`
  return `${abs.replace(/^http/, 'ws')}/dji/live/${encodeURIComponent(sn)}/sticks?token=${encodeURIComponent(token)}`
}

export function useStickSocket(onFehler: (text: string) => void) {
  const ws = useRef<WebSocket | null>(null)
  const cbFehler = useRef(onFehler)
  cbFehler.current = onFehler
  const [offen, setOffen] = useState(false)
  const [latenzMs, setLatenzMs] = useState<number | null>(null)

  const schliessen = useCallback(() => {
    const s = ws.current
    ws.current = null
    setOffen(false); setLatenzMs(null)
    if (!s) return
    s.onopen = s.onmessage = s.onerror = s.onclose = null
    try {
      if (s.readyState === WebSocket.OPEN) s.send(JSON.stringify({ x: 0, y: 0, z: 0, r: 0, stufe: 1 }))   // zuletzt: Stillstand
      s.close()
    } catch { /* schon zu */ }
  }, [])

  const oeffnen = useCallback((sn: string) => {
    schliessen()
    const token = zugangstoken()
    if (!token) { cbFehler.current('Handsteuerung nicht möglich: nicht angemeldet.'); return }
    let s: WebSocket
    try { s = new WebSocket(socketUrl(sn, token)) } catch { cbFehler.current('Handsteuerung: Verbindung konnte nicht aufgebaut werden.'); return }
    ws.current = s
    const frist = setTimeout(() => { if (ws.current === s && s.readyState !== WebSocket.OPEN) cbFehler.current('Handsteuerung: keine Antwort vom Server (Zeitüberschreitung).') }, 6000)
    s.onopen = () => { clearTimeout(frist); if (ws.current === s) setOffen(true) }
    s.onmessage = ev => {
      try {
        const m = JSON.parse(String(ev.data))
        if (m.ok === false) { cbFehler.current(`Handsteuerung: ${m.fehler || 'Server meldet einen Fehler.'}`); return }
        if (typeof m.latenz_ms === 'number') setLatenzMs(m.latenz_ms)
      } catch { /* unbekannte Nachricht ignorieren */ }
    }
    s.onerror = () => { clearTimeout(frist); if (ws.current === s) cbFehler.current('Handsteuerung: Verbindungsfehler.') }
    s.onclose = () => { clearTimeout(frist); if (ws.current === s) cbFehler.current('Handsteuerung: Verbindung vom Server geschlossen.') }
  }, [schliessen])

  /** true, wenn tatsächlich gesendet wurde */
  const senden = useCallback((b: StickBefehl) => {
    const s = ws.current
    if (!s || s.readyState !== WebSocket.OPEN) return false
    s.send(JSON.stringify(b))
    return true
  }, [])

  useEffect(() => schliessen, [schliessen])
  return { offen, latenzMs, oeffnen, senden, schliessen }
}
